import { Injectable } from '@angular/core';
import { parentCodeSchema } from '@shared/ragai.schemas';
import { z } from 'zod';

/**
 * Führt Code im einbettenden Elternfenster aus (`RAGAI.PARENT`), z. B. wenn der Bot als Fusion-View/iFrame läuft.
 * @remarks
 * {@link exec} sendet den Code nur per `postMessage` (Fire-and-forget, kein Rückgabewert). {@link get} tut
 * dasselbe, legt aber zusätzlich eine `requestId` in {@link pending} ab und wartet auf ein `exec-result`-Message-Event
 * mit derselben ID, um das Promise aufzulösen - die Gegenstelle dafür lauscht in
 * `IframeCommunicationService`, die `pending` bei Empfang der Antwort per ID auflöst und den Eintrag entfernt.
 * `postMessage` nutzt bewusst `'*'` als Ziel-Origin (nicht auf die tatsächliche Parent-Origin eingeschränkt) -
 * ein bekanntes, im Code markiertes Sicherheits-TODO.
 */
@Injectable({
  providedIn: 'root'
})
export class ParentManagerService {
  public pending = new Map<string, (value: unknown) => void>();
  /** Führt Code im Elternfenster aus (fire-and-forget, kein Rückgabewert). */
  public exec(code: string | z.input<typeof parentCodeSchema>): void {
    const parsed = parentCodeSchema.safeParse(code);
    if (parsed.success && typeof code === 'object') {
      code = parsed.data.code;
    }
    window.parent.postMessage(
      { type: 'exec', code: code as string },
      '*' // Besser: die parent-Origin eintragen!
    );
  }

  /** Führt Code im Elternfenster aus und wartet per `exec-result`-Message auf den Rückgabewert. */
  public get(code: string | z.input<typeof parentCodeSchema>): Promise<unknown> {
    const parsed = parentCodeSchema.safeParse(code);
    if (parsed.success && typeof code === 'object') {
      code = parsed.data.code;
    }
    const requestId = crypto.randomUUID();
    return new Promise(resolve => {
      this.pending.set(requestId, resolve);
      window.parent.postMessage(
        { type: 'exec-get', code: code as string, requestId },
        '*'
      );
    });
  }
}
